import { useEffect, useState, type ReactNode } from "react";
import { Btn, Checkbox, Icon, ProvenanceChip, SegmentedControl, T } from "@v0y4ge/jrni-ui";
import { RowActions } from "./composites";
import { can } from "./access";
import { toast } from "./toast";
import type { NavApi } from "./router";
import { useT, t as i18nT } from "../lib/i18n";

/**
 * Shared list-screen composites — verbatim ports of the prototype's list
 * helpers, reused by Events, Templates, Tickets, Landing Pages, Hubs, Archive.
 */

/** Persisted list/grid view toggle. */
export function useViewMode(key: string, dflt: "list" | "grid" = "list"): [("list" | "grid"), (m: "list" | "grid") => void] {
  // `jrni.view.<key>.v1` (2026-08-19, §6). No migration from the old
  // `jrni_view_<key>`: it held a view preference, not work. A value that is not
  // one of the two modes is discarded silently and the default stands.
  const lsKey = `jrni.view.${key}.v1`;
  const [mode, setMode] = useState<"list" | "grid">(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      return raw === "list" || raw === "grid" ? raw : dflt;
    } catch { return dflt; }
  });
  const set = (m: "list" | "grid") => { setMode(m); try { localStorage.setItem(lsKey, m); } catch { /* noop */ } };
  return [mode, set];
}

/**
 * ViewToggle — list/cards switch. A THIN ADAPTER over jrni-ui's `SegmentedControl`,
 * not a parallel control (migrated 2026-08-10).
 *
 * It used to hand-roll two <button>s inside a bordered div, which meant every list
 * screen in the app rendered a segmented control the design system already ships —
 * and it carried three defects no audit could see, because app/src/shell is outside
 * both audit roots: `background: on ? T.p1 : T.white` (BOTH tokens invert, so the
 * active segment flipped polarity in dark instead of staying a brand fill),
 * `borderRadius: 8` as a raw literal, and height 30 / padding 11 / gap 5 all off the
 * 4px scale. SegmentedControl owns all of that, plus roving-tabindex keyboard nav and
 * aria-pressed, which the fork only half-had.
 *
 * The public API (`mode` / `onChange` / `testid`) is unchanged, so no call site moves.
 */
export function ViewToggle({ mode, onChange, testid }: { mode: string; onChange: (m: "list" | "grid") => void; testid?: string }) {
  return (
    <SegmentedControl<"list" | "grid">
      size="sm"
      value={mode === "grid" ? "grid" : "list"}
      onChange={onChange}
      ariaLabel={i18nT("shell.listComposites.viewLabel", "View")}
      testid={testid}
      options={[
        { value: "list", label: "List", icon: <Icon.list size={13} /> },
        { value: "grid", label: "Cards", icon: <Icon.grid size={13} /> },
      ]}
    />
  );
}

export interface RowSelection {
  selected: Set<string>;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
  allChecked: boolean;
  someChecked: boolean;
}
export function useRowSelection(ids: string[]): RowSelection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => (prev.size === ids.length && ids.length ? new Set<string>() : new Set(ids)));
  const clear = () => setSelected(new Set());
  const allChecked = ids.length > 0 && selected.size === ids.length;
  const someChecked = selected.size > 0 && !allChecked;
  return { selected, toggle, toggleAll, clear, allChecked, someChecked };
}

/**
 * BulkActionBar — the selection bar for the list screens whose tables are still
 * hand-rolled. Repainted 2026-08-11 onto the DS's OWN selection-toolbar
 * treatment.
 *
 * jrni-ui does ship a selection toolbar: `DataTable`'s `bulkActions` slot —
 * a `role="toolbar"` bar carrying the select-all box, an "N selected" count
 * with an overridable noun, an arbitrary-children action slot and a Clear
 * affordance, painted `T.p4` on a `T.p2` border (EventsInlineTable already
 * uses it). So this composite must not be a SECOND answer: the screens that
 * still own their <table> get the same bar, and any screen that moves onto
 * DataTable inherits the identical treatment for free.
 *
 * What it fixes at the same time: the bar used to paint `background: T.n1`
 * with a raw `color: "#fff"` (and `rgba(255,255,255,…)` washes). `T.n1` is
 * the darkest TEXT token — it inverts to near-white in dark mode — while the
 * white literals do not invert at all, so in dark the whole bar rendered
 * white-on-white and the count, the divider and every action label vanished.
 * The brand tint + brand ink pair inverts together, in both themes. The action
 * buttons are now real `Btn`s (ghost / danger) instead of raw <button>s.
 */
/** `disabled` + `title` (2026-09-11): a bulk action whose op is not served yet
 *  renders DISABLED naming what it waits on, per the no-fake-interactivity rule —
 *  the Staff roster had forked its own bar to get this. */
export interface BulkAction { label: string; icon?: ReactNode; danger?: boolean; disabled?: boolean; title?: string; onClick: () => void; }
export function BulkActionBar({ count, noun = "item", actions = [], onClear, style }: { count: number; noun?: string; actions?: BulkAction[]; onClear: () => void; style?: React.CSSProperties }) {
  const t = useT();
  if (!count) return null;
  return (
    <div role="toolbar" aria-label={t("shell.listComposites.bulkActions", "Bulk actions")}
      style={{ margin: "0 24px 12px", padding: "6px 12px", background: T.p4, border: `1px solid ${T.p2}`, color: T.p1, borderRadius: T.radius.md, display: "flex", alignItems: "center", gap: 12, boxShadow: T.shadowMd, ...style }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{count} {t("shell.listComposites.selected", "selected")}</span>
      <span style={{ width: 1, height: 18, background: T.p2 }} />
      {actions.map((a, i) => (
        <Btn key={i} variant="ghost" size="sm" danger={a.danger} icon={a.icon} disabled={a.disabled} title={a.title} onClick={a.onClick}>{a.label}</Btn>
      ))}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 12, color: T.soft }}>{noun}{count === 1 ? "" : t('shell.listComposites.text')}</span>
      <Btn variant="ghost" size="sm" onClick={onClear}>{t("common.actions.clear", "Clear")}</Btn>
    </div>
  );
}

/** Card footer action cluster — the standard View/Edit/Duplicate/⋯ row, left-aligned meta. */
export function CardActionBar({ left, ...rowProps }: { left?: ReactNode } & React.ComponentProps<typeof RowActions>) {
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ padding: "8px 12px", borderTop: `1px solid ${T.border}`, background: T.n6, display: "flex", alignItems: "center", justifyContent: left ? "space-between" : "flex-end", gap: 8 }}>
      {left ? <div style={{ minWidth: 0, fontSize: 12, color: T.soft, display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{left}</div> : null}
      <RowActions size={28} {...rowProps} />
    </div>
  );
}

/** Shared card-grid columns token. */
export const CARD_GRID = "repeat(auto-fill, minmax(300px, 1fr))";

/* ── On-site (Event Day) provenance chips ────────────────────────────────
 * Ported 2026-07-03 from the prototype's coord-link.jsx; user-facing copy
 * renamed 2026-07-14 (docket #6): Coord is deprecated — it folded into Studio
 * as the EVENT DAY feature, and a retired product name must not surface in the
 * UI. Component/export names keep the Coord spelling for call-site stability
 * (code-internal only). Use <SharedWithCoord/> on a record Studio authors that
 * Event Day reads on-site, and <SyncedFromCoord/> on data the live floor
 * generated. `note` adds a detail (shown as the native title tooltip). */
export function SharedWithEventDay({ object = "record", note }: { object?: string; note?: string }) {
  const t = useT();
  return (
    <ProvenanceChip kind="authored" system="Event Day"
      note={note || `Studio authors this ${object}; Event Day reads it on-site.`}>
      {t("shell.listComposites.sharedWithEventDay", "Shared with Event Day")}
    </ProvenanceChip>
  );
}
export function LiveFromEventDay({ note }: { note?: string }) {
  const t = useT();
  return (
    <ProvenanceChip kind="generated" system="Event Day"
      note={note || "Generated live by Event Day on-site; Studio aggregates it."}>
      {t("shell.listComposites.liveFromEventDay", "Live from Event Day")}
    </ProvenanceChip>
  );
}
/* Deprecated spellings. "Coord" is a retired product name; the visible copy was
   corrected long ago and the exports followed on 2026-08-19. Kept as aliases for
   one pass so no call site breaks — delete once the last import is renamed. */
export const SharedWithCoord = SharedWithEventDay;
export const SyncedFromCoord = LiveFromEventDay;

/** Tab-level export. With rows: builds a real CSV from the passed records and
 *  downloads it — success toast only after the download starts. Without rows:
 *  an HONEST declared-limitation notice (the old "Export started · we'll email
 *  a CSV" claim was fake — addendum item 5 / UIR-46; no export op exists for
 *  these tabs, REQ-084). Callers should pass their visible rows. */
export function tabExport(noun: string, rows?: Record<string, unknown>[]) {
  if (!rows || rows.length === 0) {
    toast({ tone: "limitation", title: i18nT("shell.listComposites.exportNotWiredHere", "Export isn't wired on this tab yet"), subtitle: i18nT("shell.listComposites.noExportOperationIsDeclared", "No export operation is declared for this data (REQ-084) — nothing was exported.") });
    return;
  }
  const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => { const v = r[k]; if (["string", "number", "boolean"].includes(typeof v)) s.add(k); }); return s; }, new Set<string>()));
  const esc = (v: unknown) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  const slug = noun.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "export";
  a.href = url; a.download = `${slug}.csv`; a.rel = "noopener"; a.style.display = "none";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  toast({ tone: "success", title: i18nT("shell.listComposites.exportDownloaded", "Export downloaded"), subtitle: `${rows.length} ${noun} · ${slug}.csv` });
}

/** The Journeys-section tab bar.
 *
 *  ONE tab as of 2026-09-09: Manage Pages was promoted out of this strip and onto
 *  the rail as its own module, so the strip no longer has a second destination.
 *  It is kept (rather than deleted along with the tab) because it is the section's
 *  identity bar — it names where you are and carries the count — and because a
 *  future Journeys-section surface lands back in it.
 *
 *  The Journeys tab carries no count: the journeys list DERIVES its rows from
 *  queues, events and the appointment catalogue, so there is no single number to
 *  show without loading all three. `counts` is still accepted for that reason.
 *
 *  Do NOT re-add a confirmpages tab here. Two homes for one surface means the
 *  rail highlight and the tab highlight disagree — clicking the tab would leave
 *  the section it appears to belong to. */
export function journeysSectionTabs(nav: NavApi, active: string, _counts: Partial<Record<string, number>> = {}) {
  return [
    { id: "newjourneys", label: i18nT("shell.listComposites.journeys", "Journeys"), onClick: () => nav.go("newjourneys") },
  ].map((t) => ({ ...t, active: t.id === active }));
}

/** The Events-section tab bar — identical across every event-section screen.
 *  Pass the full useEventSectionCounts() result so every tab shows its live
 *  count on every screen. Calendar is a view (no collection count). */
export function eventsSectionTabs(nav: NavApi, active: string, counts: Partial<Record<string, number>> = {}) {
  return [
    { id: "events", label: i18nT("shell.listComposites.events", "Events"), count: counts.events, onClick: () => nav.go("events") },
    { id: "event-hubs", label: i18nT("shell.listComposites.eventHubs", "Event Hubs"), count: counts.hubs, onClick: () => nav.go("hub.pages") },
    /* Event Templates and Landing Pages were removed from this bar on
       2026-08-24 (operator): the Journey designer replaces both — a template is
       a starting point in its picker, and a landing page is the journey's entry
       page. Both routes still exist for deep links and for the wizard, which is
       decommissioned last. */
    { id: "tickets", label: i18nT("shell.listComposites.tickets", "Tickets"), count: counts.tickets, onClick: () => nav.go("ticket-types") },
    { id: "archived", label: i18nT("shell.listComposites.archive", "Archive"), count: counts.archived, onClick: () => nav.go("events.archived") },
    /* Attendees (2026-09-10 customers drop) — the tenant-wide Guests CRM, which
       lost its rail item when the directory became one surface. It belongs to
       the Events section: guests are the people an event has. Gated on
       `guests.read`, the same capability the routes carry, so a role without it
       sees no dead tab. Filtered out entirely rather than disabled: a tab that
       cannot be opened is noise, not information. */
    ...(can("guests.read")
      ? [{ id: "attendees", label: i18nT("shell.listComposites.attendees", "Attendees"), count: counts.attendees, onClick: () => nav.go("guests") }]
      : []),
  ].map((t) => ({ ...t, active: t.id === active }));
}
